/**
 * Encapsulates all data loading, side effects, and the save/navigate handler
 * for the medical PA form layout so the page component stays presentational.
 *
 * @module medicalPaForm/hooks/useMedicalPaFormLayout
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { openModal } from "risa-oasis-ui_v2";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { usePharmaFormFields } from "../../../context/pharmaFormFieldsContext";
import { ModalId } from "../../../enums/modalId";
import useDumpLatestMedicalPaFormData from "../../../hooks/useDumpLatestMedicalPaFormData";
import useFetchQuestionnaire from "../../../hooks/useFetchQuestionnaire";
import useLatestMedicalPaFormData from "../../../hooks/useLatestMedicalPaFormData";
import { fetchNccnGuidelinesDocuments } from "../../../redux/slice/medicalPa/guidelinesDocumentsSlice";
import { fetchMedicalPaClinicalData } from "../../../redux/slice/medicalPa/medicalPaClinicalDataSlice";
import { getMedicalPaOrderDetailsFor } from "../../../redux/slice/medicalPa/singleMedicalPaOrder";
import { fetchWorkflowConfigDataForAnOrder } from "../../../redux/slice/workflowConfigSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { clearFaxPdfPoller } from "../../../utils/getFaxPdfLinkRecursively";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { saveOrderAndNavigate } from "../components/utils/buildAndSaveOrderData";
import { UseMedicalPaFormLayoutReturn } from "../types";

/**
 * Loads the order, clinical data, form config, NCCN docs, and workflow config
 * for the routed PA order, mirrors the payer name into shared context, and
 * exposes a save handler plus every value the layout needs to render.
 *
 * @returns Aggregated state and handlers; see {@link UseMedicalPaFormLayoutReturn}.
 */
const useMedicalPaFormLayout = (): UseMedicalPaFormLayoutReturn => {
  const { id } = useParams();
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const { loading: isGuidelinesDocumentsLoading, data: guidelinesDocuments } =
    useSelector((state: RootState) => state.guidelinesDocuments);
  const { setPayerName } = usePayerNameContextFromSingleOrderLayout();

  const { isLoading, unsubscribe } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const {
    data: singleMedicalPaFormConfig,
    loading: isSingleMedicalPaFormConfigLoading,
  } = useSelector((state: RootState) => state.singleMedicalPaFormConfig);

  const { isLoading: isWorkflowConfigLoading } = useSelector(
    (state: RootState) => state.workflowConfig,
  );

  const {
    loading: isMedicalPaClinicalDataLoading,
    unsubscribe: medicalPaClinicalDataUnsubscribe,
  } = useSelector((state: RootState) => state.medicalPaClinicalData);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgId = getOrgIdForFetchExternalWorklist();

  const {
    data: healthCareFacilityData,
    loading: isHealthCareFacilityLoading,
    error: healthCareFacilityError,
  } = useSelector((state: RootState) => state.singleHealthFacilityDetails);
  const {
    submissionData,
    fetchAll,
    abortAll,
    coverageDetails,
    workflowConfig,
    authOnFileDetails,
    generalOrderDetails,
    loading: isDataAccumulatorLoading,
  } = useDataAccumulatorForSingleOrder();

  const { loading: medicalPaFormConfigLoading } = useSelector(
    (state: RootState) => state.medicalPaFormConfig,
  );
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { formFieldsData, isAllFieldsValidationFree, setFormDirty } =
    usePharmaFormFields();
  const { hasQuestions } = useFetchQuestionnaire({
    orderId: id ?? "",
  });
  const {
    data: latestMedicalPaFormData,
    loading: isLatestMedicalPaFormDataLoading,
  } = useLatestMedicalPaFormData({
    orderId: id ?? "",
    orgId: orgId ?? "",
    formId: singleMedicalPaFormConfig?.id ?? "",
  });
  const { dumpFormData } = useDumpLatestMedicalPaFormData();
  const [isSaving, setIsSaving] = useState(false);
  const shouldRunMedicalNeccessity = useRef<boolean>(false);

  const faxPdfIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const orderUnsubscribeRef = useRef<(() => void) | undefined>(undefined);
  const clinicalDataUnsubscribeRef = useRef<(() => void) | undefined>(
    undefined,
  );

  // Both slices only publish their unsubscriber from inside the Firestore
  // snapshot callback, so the listener effect below closes over `undefined` on
  // mount. Mirror them into refs so the cleanup can reach the live listeners.
  useEffect(() => {
    orderUnsubscribeRef.current = unsubscribe;
  }, [unsubscribe]);

  useEffect(() => {
    clinicalDataUnsubscribeRef.current = medicalPaClinicalDataUnsubscribe;
  }, [medicalPaClinicalDataUnsubscribe]);

  useEffect(() => {
    if (id) {
      dispatch(getMedicalPaOrderDetailsFor(id));
      dispatch(fetchMedicalPaClinicalData(id));
      dispatch(fetchNccnGuidelinesDocuments({ order_id: id }));
      dispatch(fetchWorkflowConfigDataForAnOrder(id));
    }
    return () => {
      orderUnsubscribeRef.current?.();
      orderUnsubscribeRef.current = undefined;
      clinicalDataUnsubscribeRef.current?.();
      clinicalDataUnsubscribeRef.current = undefined;
      clearFaxPdfPoller(faxPdfIntervalRef);
    };
  }, [dispatch, id]);

  // Fan out the data-accumulator's parallel domain fetches whenever the route
  // id (or org) changes; abort any in-flight requests on unmount.
  useEffect(() => {
    let isMounted = true;
    if (id && isMounted) {
      fetchAll(id, orgId);
    }
    return () => {
      isMounted = false;
      abortAll();
    };
  }, [id, orgId]);

  // Mirror the first coverage's payer name into the shared payer context so
  // descendants (analytics, three-dots menu) can read it without prop drilling.
  useEffect(() => {
    if (coverageDetails) {
      const primaryCoverage = coverageDetails?.coverages?.find(
        (coverage) => coverage.coverage_type === "primary",
      );
      setPayerName(primaryCoverage?.payer_name ?? "");
    }
  }, [coverageDetails]);

  const updateSingleOrderIdWithFormData = async (shouldSaveOnly?: boolean) => {
    shouldSaveOnly ? setIsSaving(true) : setLoading(true);
    try {
      await saveOrderAndNavigate({
        id: id ?? "",
        formFieldsData,
        latestMedicalPaFormData: latestMedicalPaFormData ?? {},
        generalOrderDetails,
        authOnFileDetails,
        dumpFormData,
        singleMedicalPaFormConfig,
        workflowConfig,
        user,
        orgId,
        navigate,
        faxPdfIntervalRef,
        setLoading,
        setFormDirty,
        hasQuestions,
        shouldRunMedicalNeccessity: shouldRunMedicalNeccessity.current,
        shouldSaveOnly: shouldSaveOnly ?? false,
      });
      toast.success(ToastMessages.FORM_SAVE_SUCCESS);
    } catch (error) {
      toast.error(ToastMessages.FORM_SAVE_FAILURE);
    } finally {
      if (workflowConfig?.submission_type !== "fax") {
        setLoading(false);
      }
      if (shouldSaveOnly) {
        setIsSaving(false);
      }
    }
  };

  useEffect(() => {
    if (openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION) {
      openModal(ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION);
    }
  }, [openedModalId]);

  const isPageLoading =
    medicalPaFormConfigLoading ||
    isSingleMedicalPaFormConfigLoading ||
    isMedicalPaClinicalDataLoading ||
    isGuidelinesDocumentsLoading ||
    isDataAccumulatorLoading ||
    isWorkflowConfigLoading ||
    isLatestMedicalPaFormDataLoading;

  return {
    id,
    isPageLoading,
    activeSectionId,
    setActiveSectionId,
    generalOrderDetails,
    authOnFileDetails,
    submissionData,
    guidelinesDocuments,
    healthCareFacilityData: {
      data: healthCareFacilityData,
      loading: isHealthCareFacilityLoading,
      error: healthCareFacilityError ?? null,
    },
    singleMedicalPaFormConfig,
    latestMedicalPaFormData,
    workflowConfig,
    isSaving,
    loading,
    isAllFieldsValidationFree,
    isLoading: isLoading ?? false,
    hasQuestions,
    shouldRunMedicalNeccessity,
    navigate,
    updateSingleOrderIdWithFormData,
  };
};

export default useMedicalPaFormLayout;
